import { useState, useEffect } from 'react';
import supabase from '../supabase';
import { T, GROQ_MODEL_FAST } from '../constants';

// managed_attractions only stores one Traditional-Chinese `description` per spot — no
// localized variants. This hook translates it into the active UI language on demand
// (mirroring useAttractionPhoto's visibility-gated fetch), cached locally and in Supabase
// ai_cache (keyed by content fingerprint + lang) so each unique description is ever
// translated by AI once, shared across all users.

const AI_CACHE_PFX = 'ec_ai_';
const TTL_DESC = 3 * 24 * 60 * 60 * 1000; // 3 days — matches EC_Travel_v2's TTL_ATTR

const fingerprint = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
};

const getLocalCache = (key) => {
  try {
    const item = JSON.parse(localStorage.getItem(AI_CACHE_PFX + key));
    if (!item || Date.now() > item.exp) { localStorage.removeItem(AI_CACHE_PFX + key); return null; }
    return item.data;
  } catch { return null; }
};
const setLocalCache = (key, data) => {
  try { localStorage.setItem(AI_CACHE_PFX + key, JSON.stringify({ data, exp: Date.now() + TTL_DESC })); } catch {}
};
const getServerCache = async (key) => {
  try {
    const { data, error } = await supabase.from('ai_cache').select('data,expires_at').eq('cache_key', key).maybeSingle();
    if (error || !data) return null;
    if (new Date(data.expires_at) < new Date()) return null;
    return data.data;
  } catch { return null; }
};
const setServerCache = async (key, data) => {
  try {
    await supabase.from('ai_cache').upsert(
      { cache_key: key, data, expires_at: new Date(Date.now() + TTL_DESC).toISOString() },
      { onConflict: 'cache_key' }
    );
  } catch {}
};

// Minimum gap between Groq calls fired from this hook — many cards can become visible
// at once, and we don't want to burst past the free-tier TPM limit.
let _lastCall = 0;
const _throttle = () => {
  const wait = Math.max(0, 1500 - (Date.now() - _lastCall));
  _lastCall = Date.now() + wait;
  return wait > 0 ? new Promise(r => setTimeout(r, wait)) : Promise.resolve();
};

const _mem = {};      // cacheKey -> translated text | null (resolved)
const _inflight = {}; // cacheKey -> in-flight promise, dedupes concurrent card requests

async function translate(text, lang, cacheKey, al) {
  if (_inflight[cacheKey]) return _inflight[cacheKey];
  _inflight[cacheKey] = (async () => {
    const local = getLocalCache(cacheKey);
    if (local) { _mem[cacheKey] = local; return local; }
    const server = await getServerCache(cacheKey);
    if (server) { setLocalCache(cacheKey, server); _mem[cacheKey] = server; return server; }

    await _throttle();
    let out = null;
    try {
      const r = await fetch('/api/groq', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: GROQ_MODEL_FAST,
          max_tokens: 280,
          messages: [
            { role: 'system', content: `Translate the user's short tourist-attraction description from Traditional Chinese into ${al}. Reply with ONLY the translated text — no quotes or extra commentary.` },
            { role: 'user', content: text },
          ],
        }),
      });
      if (r.ok) {
        const d = await r.json();
        out = (d.choices?.[0]?.message?.content || '').trim().replace(/^["'「『]+|["'」』]+$/g, '') || null;
      }
    } catch {}
    _mem[cacheKey] = out;
    if (out) { setLocalCache(cacheKey, out); setServerCache(cacheKey, out); }
    return out;
  })();
  try { return await _inflight[cacheKey]; } finally { delete _inflight[cacheKey]; }
}

const useTranslatedDesc = (text, lang, isVisible = true) => {
  const al = T[lang]?.aiLang;
  const skip = !text || !al || lang === 'zh-HK';
  const cacheKey = skip ? null : `attr_desc_${fingerprint(text)}_${lang}`;
  const [, bump] = useState(0);

  useEffect(() => {
    if (skip || !isVisible || !cacheKey) return;
    if (_mem[cacheKey] !== undefined) return;
    let cancelled = false;
    translate(text, lang, cacheKey, al).then(() => { if (!cancelled) bump(n => n + 1); });
    return () => { cancelled = true; };
  }, [skip, isVisible, cacheKey, text, lang, al]);

  if (skip) return text;
  const cached = _mem[cacheKey];
  return (cached !== undefined && cached) ? cached : text;
};

export default useTranslatedDesc;
